import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState, Spinner } from "@/components/common";
import { toast } from "sonner";
import { TrendingUp, Search, Sparkles, Target } from "lucide-react";

function ScoreBar({ label, value, tone }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] text-slate-400 mb-1"><span>{label}</span><span className="font-mono">{value}</span></div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function Research() {
  const [direction, setDirection] = useState("");
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/research").then(({ data }) => setNiches(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/research/generate", { direction });
      setNiches((prev) => [...data, ...prev]);
      toast.success(`Research agent found ${data.length} niche opportunities.`);
    } catch (e) {
      toast.error(apiError(e, "Research failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Niche & Trend Research" subtitle="The research agent finds low-competition, high-demand POD opportunities. Fully autonomous — no money involved." testid="research-header">
        <Pill tone="emerald">Full Auto</Pill>
      </PageHeader>

      <Card className="p-5" data-testid="research-input-card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus-within:border-emerald-500/60">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              data-testid="research-direction-input"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && generate()}
              placeholder="Optional niche direction (e.g. 'cozy cat lovers', 'trail running') — or leave blank to let the agent decide"
              className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
            />
          </div>
          <button data-testid="run-research-button" onClick={generate} disabled={loading} className="px-5 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center">
            {loading ? <Spinner label="Researching…" /> : <><Sparkles className="w-4 h-4" /> Run Research</>}
          </button>
        </div>
      </Card>

      {niches.length === 0 && !loading ? (
        <Card className="p-6"><EmptyState icon={TrendingUp} title="No research yet" hint="Run the research agent to surface niche opportunities with demand and competition scores." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {niches.map((n) => (
            <Card key={n.id} className="p-5 hover:border-emerald-500/30 transition-colors" data-testid={`niche-card-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-head text-lg font-semibold text-slate-100">{n.niche}</h3>
                  <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Target className="w-3 h-3" /> {n.audience}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-head font-bold text-emerald-400">{n.opportunity_score}</div>
                  <div className="text-[10px] text-slate-500 font-mono uppercase">Opportunity</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <ScoreBar label="Demand" value={n.demand} tone="bg-emerald-500" />
                <ScoreBar label="Competition" value={n.competition} tone="bg-amber-500" />
              </div>
              {n.rationale && <p className="text-sm text-slate-400 mt-4 leading-relaxed">{n.rationale}</p>}
              {n.product_ideas?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {n.product_ideas.map((p, i) => <Pill key={i} tone="blue">{p}</Pill>)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
